'use client'

/**
 * OutreachAccountPicker — connected sending accounts (spec §4, §11, §16).
 *
 * Lists each Unipile-connected account with a status chip, today's usage vs cap,
 * channel + provider icon, an expandable caps/window editor, and a "Connect
 * account" action that kicks off hosted-auth (opens the returned URL; in
 * simulated mode the account comes back already connected).
 *
 * Quiet cards on --canvas-base; coral only on the primary Connect button.
 * Every list has explicit empty / loading / error states (Lantern tokens).
 */

import { useEffect, useState } from 'react'
import {
  Mail,
  Plus,
  ChevronDown,
  ChevronUp,
  Trash2,
  AlertCircle,
  RefreshCw,
  Inbox,
} from 'lucide-react'
import { LinkedinBrand, GmailBrand, WhatsAppBrand, OutlookBrand } from './BrandIcons'
import { Button, Badge, Input, Modal, ModalHeader, ModalBody, ModalFooter } from '@/components/ui-v2'
import { useOutreachStore } from '../store/useOutreachStore'
import { accountsApi } from '../services/outreach.api'
import { STATUS_META, PROVIDER_META, humanizeAccountError } from '../constants'
import type { OutreachAccount, AccountProvider, AccountChannel, ConnectAccountPayload, UpdateAccountPayload, AccountAnalytics } from '../types'
import { CapsWindowEditor } from './CapsWindowEditor'
import { timeAgo } from './analytics-ui'
import { buildConnectAccountPayload } from '../user-timezone'

type Props = {
  /** Show the inline caps/window editor under each account. The Caps & Safety
   *  tab opens this; the Accounts tab keeps it collapsible (default). */
  defaultExpanded?: boolean
  /** Fetch + render per-account usage vs cap + health signals (§13 account
   *  analytics). On in Settings; off in the campaign-setup connect prompt. */
  showAnalytics?: boolean
}

const BADGE_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'default'> = {
  pending: 'default',
  connected: 'success',
  warming: 'warning',
  limited: 'warning',
  disconnected: 'default',
  error: 'danger',
  on_hold: 'warning',
}

// Statuses that need a "Reconnect" affordance (revive the existing row in place).
const RECONNECTABLE = new Set(['disconnected', 'error', 'limited'])

export function OutreachAccountPicker({ defaultExpanded = false, showAnalytics = false }: Props) {
  const {
    accounts,
    accountsStatus,
    accountsError,
    loadAccounts,
    updateAccount,
    refreshLinkedInFeatures,
    removeAccount,
    reconnectAccount,
    claimAccount,
  } = useOutreachStore()

  const [connectOpen, setConnectOpen] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [analyticsById, setAnalyticsById] = useState<Record<string, AccountAnalytics>>({})

  useEffect(() => {
    if (accountsStatus === 'idle') loadAccounts()
  }, [accountsStatus, loadAccounts])

  // Pending-connect poller (Stage 1): while any row is still finishing hosted-auth,
  // poll so it flips to connected/on_hold (or gets GC'd) without a manual reload.
  // Bounded to ~2 min so an abandoned connect doesn't poll forever.
  const hasPending = accounts.some((a) => a.status === 'pending')
  useEffect(() => {
    if (!hasPending) return
    let ticks = 0
    const id = window.setInterval(() => {
      ticks += 1
      loadAccounts()
      if (ticks >= 30) window.clearInterval(id) // 30 × 4s ≈ 2 min
    }, 4000)
    const onFocus = () => loadAccounts()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [hasPending, loadAccounts])

  // Per-account usage vs cap + health (§13). Best-effort: a failed fetch just
  // leaves the row on its at-rest cap values.
  useEffect(() => {
    if (!showAnalytics || accountsStatus !== 'loaded' || accounts.length === 0) return
    let cancelled = false
    Promise.all(
      // Skip pending connects — they have no usage/health to report yet.
      accounts
        .filter((a) => a.status !== 'pending')
        .map((a) =>
          accountsApi
            .analytics(a._id)
            .then(({ data }) => [a._id, data] as const)
            .catch(() => null)
        )
    ).then((results) => {
      if (cancelled) return
      const next: Record<string, AccountAnalytics> = {}
      for (const r of results) if (r) next[r[0]] = r[1]
      setAnalyticsById(next)
    })
    return () => {
      cancelled = true
    }
  }, [showAnalytics, accountsStatus, accounts])

  const loading = accountsStatus === 'loading'
  const isExpanded = (id: string) => defaultExpanded || expandedId === id

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-[15px] font-medium" style={{ color: 'var(--text-primary)' }}>
            Connected accounts
          </h3>
          <p className="text-[13px] mt-0.5" style={{ color: 'var(--text-secondary)' }}>
            Email and LinkedIn accounts used to send outreach. Each stays under its own safe caps.
          </p>
        </div>
        <Button variant="primary" size="md" onClick={() => setConnectOpen(true)}>
          <Plus size={15} strokeWidth={2} />
          Connect account
        </Button>
      </div>

      {/* Error state */}
      {accountsError && (
        <div
          className="flex items-center justify-between gap-3 p-3 rounded-lg"
          style={{ background: 'var(--bg-danger)', color: 'var(--fg-danger)' }}
        >
          <span className="flex items-center gap-2 text-[13px]">
            <AlertCircle size={15} strokeWidth={2} />
            {accountsError}
          </span>
          <button
            onClick={() => loadAccounts()}
            className="flex items-center gap-1 text-[12px] font-medium"
          >
            <RefreshCw size={12} strokeWidth={2} /> Retry
          </button>
        </div>
      )}

      {/* Loading state */}
      {loading && accounts.length === 0 && (
        <div className="flex flex-col gap-2">
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-[68px] rounded-[var(--radius-lg)] animate-pulse"
              style={{ background: 'var(--surface-tertiary)' }}
            />
          ))}
        </div>
      )}

      {/* Empty state */}
      {accountsStatus === 'loaded' && accounts.length === 0 && !accountsError && (
        <div
          className="flex flex-col items-center text-center py-12 px-6 rounded-[var(--radius-lg)]"
          style={{ background: 'var(--surface-primary)', border: '1px dashed var(--border-emphasis)' }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
            style={{ background: 'var(--surface-tertiary)', color: 'var(--text-tertiary)' }}
          >
            <Inbox size={18} strokeWidth={1.75} />
          </div>
          <p className="text-[14px] font-medium" style={{ color: 'var(--text-primary)' }}>
            No accounts connected yet
          </p>
          <p className="text-[13px] mt-1 mb-4 max-w-xs" style={{ color: 'var(--text-secondary)' }}>
            Connect a Gmail, Outlook, or LinkedIn account to start sending outreach safely.
          </p>
          <Button variant="primary" size="md" onClick={() => setConnectOpen(true)}>
            <Plus size={15} strokeWidth={2} />
            Connect your first account
          </Button>
        </div>
      )}

      {/* Account list */}
      {accounts.length > 0 && (
        <div className="flex flex-col gap-2.5">
          {accounts.map((account) => (
            <AccountRow
              key={account._id}
              account={account}
              analytics={showAnalytics ? analyticsById[account._id] : undefined}
              expanded={isExpanded(account._id)}
              collapsible={!defaultExpanded}
              onToggle={() =>
                setExpandedId((cur) => (cur === account._id ? null : account._id))
              }
              onSave={(payload) => updateAccount(account._id, payload)}
              onRefreshLinkedInFeatures={() => refreshLinkedInFeatures(account._id)}
              onRemove={() => removeAccount(account._id)}
              onReconnect={() => reconnectAccount(account._id)}
              onClaim={() => claimAccount(account._id)}
            />
          ))}
        </div>
      )}

      <ConnectAccountModal open={connectOpen} onClose={() => setConnectOpen(false)} />
    </div>
  )
}

/* ─── Account row ─── */

function AccountRow({
  account,
  analytics,
  expanded,
  collapsible,
  onToggle,
  onSave,
  onRefreshLinkedInFeatures,
  onRemove,
  onReconnect,
  onClaim,
}: {
  account: OutreachAccount
  analytics?: AccountAnalytics
  expanded: boolean
  collapsible: boolean
  onToggle: () => void
  onSave: (payload: UpdateAccountPayload) => Promise<void>
  onRefreshLinkedInFeatures: () => Promise<void>
  onRemove: () => Promise<void>
  onReconnect: () => Promise<string | null>
  onClaim: () => Promise<string | null>
}) {
  const [removing, setRemoving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [refreshingFeatures, setRefreshingFeatures] = useState(false)
  const status = STATUS_META[account.status]
  // A `pending` account is a hosted-auth connect the user hasn't finished. It is
  // not a real sending account yet — show it as in-progress, not as usage/caps.
  const isPending = account.status === 'pending'
  const isOnHold = account.status === 'on_hold'
  const canReconnect = RECONNECTABLE.has(account.status)

  const handleReconnect = async () => {
    setBusy(true)
    try {
      const url = await onReconnect()
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setBusy(false)
    }
  }

  const handleClaim = async () => {
    setBusy(true)
    try {
      // The claim disconnects the other workspaces, then finishes with ONE fresh
      // Google sign-in (hosted auth) — required because deleting any duplicate
      // connection revokes the shared OAuth grant, so no pre-existing connection
      // can survive a claim. The row shows "Finishing sign-in" until the webhook
      // binds the new connection.
      const url = await onClaim()
      if (url) window.open(url, '_blank', 'noopener,noreferrer')
    } finally {
      setBusy(false)
    }
  }
  // Real brand marks per channel; email rows differentiate Gmail vs Outlook by
  // provider (IMAP — currently not offered in the connect modal — falls back to
  // the Gmail-style envelope).
  const ChannelIcon =
    account.channel === 'linkedin'
      ? LinkedinBrand
      : account.channel === 'whatsapp'
        ? WhatsAppBrand
        : account.provider === 'outlook'
          ? OutlookBrand
          : GmailBrand
  const label =
    account.displayName ||
    account.emailAddress ||
    `${PROVIDER_META[account.provider].label} account`

  // Prefer live analytics usage; fall back to any usage on the account doc.
  const usage = analytics?.usage ?? account.usage
  const health = analytics?.health
  // Denominator = the warm-up-ramped cap the engine enforces (server-computed on
  // the account row), NOT the raw configured cap — so the first paint already
  // matches what analytics returns and the number never flickers (80 → 24).
  const dailyCap = usage?.dailyCap ?? account.effectiveCaps?.daily ?? account.caps.daily
  const sentToday = usage?.sentToday ?? 0
  const usagePct = dailyCap > 0 ? Math.min(100, Math.round((sentToday / dailyCap) * 100)) : 0
  const nearCap = usagePct >= 80
  const linkedinFeatures = account.linkedinFeatures
  const inmailCredits =
    linkedinFeatures == null
      ? null
      : Object.values(linkedinFeatures.inmailBalance || {}).reduce<number>(
          (sum, value) => sum + (typeof value === 'number' && value > 0 ? value : 0),
          0,
        )
  const linkedinSeats = accountLinkedInSeats(account)

  const handleRefreshLinkedInFeatures = async () => {
    setRefreshingFeatures(true)
    try {
      await onRefreshLinkedInFeatures()
    } finally {
      setRefreshingFeatures(false)
    }
  }

  const handleRemove = async () => {
    setRemoving(true)
    try {
      await onRemove()
    } finally {
      setRemoving(false)
    }
  }

  return (
    <div
      className="rounded-[var(--radius-lg)] overflow-hidden"
      style={{ background: 'var(--surface-primary)', border: '1px solid var(--border-default)' }}
    >
      <div className="flex items-center gap-3 p-3.5">
        {/* Channel + provider icon */}
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: 'var(--surface-tertiary)', color: 'var(--text-secondary)' }}
        >
          <ChannelIcon size={17} strokeWidth={1.75} />
        </div>

        {/* Identity */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
              {label}
            </span>
            <Badge variant={BADGE_VARIANT[account.status]} dot>
              {status.label}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-[12px] whitespace-nowrap" style={{ color: 'var(--text-tertiary)' }}>
              {/* Provider · channel — but the channel suffix only when it adds
                  information ("Gmail · Email"); for LinkedIn/WhatsApp the
                  provider IS the channel, so "LinkedIn · LinkedIn" is noise. */}
              {PROVIDER_META[account.provider].label}
              {account.channel === 'email' ? ' · Email' : ''}
            </span>
            {account.channel === 'linkedin' && linkedinSeats.map((seat) => (
              <span
                key={seat}
                className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                style={{ background: 'var(--accent-muted)', color: 'var(--accent-emphasis)' }}
              >
                {seat}
              </span>
            ))}
            {account.channel === 'linkedin' && inmailCredits != null && (
              <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                {inmailCredits} InMail credit{inmailCredits === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>

        {/* Usage vs cap — only for a live account, never a pending connect */}
        {!isPending && (
          <div className="hidden sm:flex flex-col items-end w-32 shrink-0">
            <span
              className="text-[12px] font-medium tabular-nums"
              style={{ color: nearCap ? 'var(--fg-warning)' : 'var(--text-secondary)' }}
            >
              {sentToday} / {dailyCap} today
            </span>
            <div
              className="w-full h-1.5 rounded-full mt-1 overflow-hidden"
              style={{ background: 'var(--surface-tertiary)' }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${usagePct}%`,
                  background: nearCap ? 'var(--fg-warning)' : 'var(--accent)',
                }}
              />
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 shrink-0">
          {account.channel === 'linkedin' && !isPending && !isOnHold && (
            <button
              type="button"
              onClick={handleRefreshLinkedInFeatures}
              disabled={refreshingFeatures}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors disabled:opacity-40"
              style={{ color: 'var(--text-tertiary)' }}
              title={
                linkedinFeatures?.checkedAt
                  ? `Refresh LinkedIn seat and credits · checked ${timeAgo(linkedinFeatures.checkedAt)}`
                  : 'Check LinkedIn seat and InMail credits'
              }
              aria-label="Refresh LinkedIn seat and InMail credits"
            >
              <RefreshCw size={14} className={refreshingFeatures ? 'animate-spin' : ''} />
            </button>
          )}
          {/* Pending connect → let the user reopen the hosted-auth window. */}
          {isPending && account.hostedAuthUrl && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                window.open(account.hostedAuthUrl, '_blank', 'noopener,noreferrer')
              }
            >
              Finish connecting
            </Button>
          )}
          {/* Disconnected/error/limited → reconnect IN PLACE (reuses this row). */}
          {canReconnect && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={handleReconnect}>
              <RefreshCw size={13} strokeWidth={2} />
              {busy ? 'Reconnecting…' : 'Reconnect'}
            </Button>
          )}
          {/* On hold (held by another workspace) → claim it here. */}
          {isOnHold && (
            <Button variant="primary" size="sm" disabled={busy} onClick={handleClaim}>
              {busy ? 'Claiming…' : 'Disconnect from other orgs'}
            </Button>
          )}
          {collapsible && !isPending && !isOnHold && (
            <button
              onClick={onToggle}
              className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--surface-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              aria-label={expanded ? 'Collapse caps editor' : 'Edit caps & window'}
            >
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
          )}
          <button
            onClick={handleRemove}
            disabled={removing}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-colors disabled:opacity-40"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--bg-danger)'
              e.currentTarget.style.color = 'var(--fg-danger)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--text-tertiary)'
            }}
            aria-label={isPending ? 'Cancel connection' : 'Remove account'}
          >
            <Trash2 size={15} strokeWidth={1.75} />
          </button>
        </div>
      </div>

      {/* Provider error — full-width line so the actionable copy is never
          truncated by the crowded header row; wraps as needed. Hover shows the
          raw provider code for debugging. */}
      {account.lastError && (
        <div
          className="px-3.5 pb-2.5 -mt-1 text-[12px] leading-snug"
          style={{ color: 'var(--fg-danger)' }}
          title={account.lastError}
        >
          {humanizeAccountError(account.lastError)}
        </div>
      )}

      {/* Usage + health (§13 account analytics) — never for a pending connect */}
      {!isPending && usage && health && (
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-3.5 pb-3 pt-0.5 text-[12px]"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <HealthStat label="Today" value={`${sentToday}/${dailyCap}`} />
          <HealthStat label="This hour" value={`${usage.sentThisHour}/${usage.hourlyCap}`} />
          {account.channel === 'linkedin' && usage.inviteDailyCap != null && (
            <HealthStat label="Invites today" value={`${usage.invitesSentToday ?? 0}/${usage.inviteDailyCap}`} />
          )}
          {account.channel === 'linkedin' && usage.inmailDailyCap != null && (
            <HealthStat label="InMails today" value={`${usage.inmailsSentToday ?? 0}/${usage.inmailDailyCap}`} />
          )}
          {health.errors > 0 && (
            <span title="Provider error events (sign-in, sync, rate-limit) plus failed sends in the last 24 hours">
              <HealthStat label="Errors" value={health.errors} danger />
            </span>
          )}
          {health.disconnects > 0 && (
            <span title="Times the provider disconnected/stopped this account recently">
              <HealthStat label="Disconnects" value={health.disconnects} danger />
            </span>
          )}
          {health.throttled > 0 && (
            <span title="Times the provider rate-limited this account recently">
              <HealthStat label="Throttled" value={health.throttled} warning />
            </span>
          )}
          <HealthStat label="Last activity" value={timeAgo(health.lastActivityAt)} />
        </div>
      )}

      {/* Caps / window editor — not for a pending connect (no caps to tune yet) */}
      {expanded && !isPending && (
        <div
          className="px-3.5 pb-4 pt-1"
          style={{ borderTop: '1px solid var(--border-muted)' }}
        >
          <CapsWindowEditor account={account} onSave={onSave} />
        </div>
      )}
    </div>
  )
}

function accountLinkedInSeats(account: OutreachAccount): string[] {
  const features = account.linkedinFeatures
  if (!features) return []
  const seats: string[] = []
  if (features.premium || features.inmailBalance.premium != null) seats.push('Premium')
  if (features.salesNavContract || features.inmailBalance.sales_navigator != null) seats.push('Sales Nav')
  if (features.recruiterContract || features.inmailBalance.recruiter != null) seats.push('Recruiter')
  return seats
}

function HealthStat({
  label,
  value,
  danger,
  warning,
}: {
  label: string
  value: string | number
  danger?: boolean
  warning?: boolean
}) {
  const color = danger ? 'var(--fg-danger)' : warning ? 'var(--fg-warning)' : 'var(--text-secondary)'
  return (
    <span className="flex items-center gap-1.5">
      <span style={{ color: 'var(--text-tertiary)' }}>{label}</span>
      <span className="font-medium tabular-nums" style={{ color }}>
        {value}
      </span>
    </span>
  )
}

/* ─── Connect modal ─── */

// IMAP intentionally omitted for now (provider stays valid server-side —
// re-add the entry here when generic-IMAP connect is a supported flow).
const PROVIDER_CHOICES: { provider: AccountProvider; label: string; channel: AccountChannel; Icon: typeof Mail }[] = [
  { provider: 'gmail', label: 'Gmail', channel: 'email', Icon: GmailBrand },
  { provider: 'outlook', label: 'Outlook', channel: 'email', Icon: OutlookBrand },
  { provider: 'linkedin', label: 'LinkedIn', channel: 'linkedin', Icon: LinkedinBrand },
  { provider: 'whatsapp', label: 'WhatsApp', channel: 'whatsapp', Icon: WhatsAppBrand },
]

function ConnectAccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const connectAccount = useOutreachStore((s) => s.connectAccount)
  const [provider, setProvider] = useState<AccountProvider>('gmail')
  const [displayName, setDisplayName] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const choice = PROVIDER_CHOICES.find((c) => c.provider === provider)!
  const isEmail = choice.channel === 'email'

  const reset = () => {
    setProvider('gmail')
    setDisplayName('')
    setEmailAddress('')
    setError(null)
    setSubmitting(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleConnect = async () => {
    setSubmitting(true)
    setError(null)
    const payload: ConnectAccountPayload = buildConnectAccountPayload({
      provider,
      channel: choice.channel,
      displayName,
      emailAddress,
    })
    try {
      const hostedAuthUrl = await connectAccount(payload)
      if (hostedAuthUrl) {
        // Hand off to Unipile hosted-auth to finish connecting.
        window.open(hostedAuthUrl, '_blank', 'noopener,noreferrer')
      }
      handleClose()
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string }
      setError(e?.response?.data?.error || e?.message || 'Failed to start connection')
      setSubmitting(false)
    }
  }

  return (
    <Modal open={open} onClose={handleClose} width={460}>
      <ModalHeader onClose={handleClose}>Connect a sending account</ModalHeader>
      <ModalBody className="flex flex-col gap-4">
        <div>
          <label className="text-[13px] font-medium mb-1.5 block" style={{ color: 'var(--text-primary)' }}>
            Provider
          </label>
          <div className="grid grid-cols-2 gap-2">
            {PROVIDER_CHOICES.map(({ provider: p, label, Icon }) => {
              const active = provider === p
              return (
                <button
                  key={p}
                  onClick={() => setProvider(p)}
                  className="flex items-center gap-2 h-10 px-3 rounded-lg text-[13px] font-medium transition-colors"
                  style={{
                    background: active ? 'var(--accent-muted)' : 'var(--surface-primary)',
                    color: active ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                    border: `1px solid ${active ? 'var(--border-accent)' : 'var(--border-default)'}`,
                  }}
                >
                  <Icon size={15} strokeWidth={1.75} />
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        <Input
          label="Display name (optional)"
          placeholder="e.g. Jordan from Acme"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />

        {isEmail && (
          <Input
            label="Email address (optional)"
            type="email"
            placeholder="you@company.com"
            value={emailAddress}
            onChange={(e) => setEmailAddress(e.target.value)}
            hint="Used as the reply-from address for this account."
          />
        )}

        <p className="text-[12px] leading-relaxed" style={{ color: 'var(--text-tertiary)' }}>
          {choice.channel === 'whatsapp'
            ? 'You’ll be taken to a secure window to link WhatsApp by scanning a QR code with your phone (WhatsApp → Linked devices). Safe sending caps are applied automatically — you can tune them afterwards.'
            : 'You’ll be taken to a secure window to authorize the account. Safe sending caps are applied automatically — you can tune them afterwards.'}
        </p>

        {error && (
          <div
            className="flex items-center gap-2 p-2.5 rounded-lg text-[12px]"
            style={{ background: 'var(--bg-danger)', color: 'var(--fg-danger)' }}
          >
            <AlertCircle size={14} strokeWidth={2} />
            {error}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="md" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" size="md" disabled={submitting} onClick={handleConnect}>
          {submitting ? 'Connecting…' : 'Connect account'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
